import { useFetch } from '#app';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { defineStore } from 'pinia';
import { routerPlugin } from '~/plugins/router-access';
import { useUsersListStore } from '~/stores/users/list';
import type {
  IAccount,
  IMaxNumber,
  INavaAccount,
  INNavaNumber,
  INNavaNumberPosted,
  INNavaNumberVerify,
  IUsersOperatorDefineStore,
} from '~/types/users/Operator';

export const useUsersOperatorStore = defineStore('UsersOperatorStore', {
  state: (): {
    error: null;
    nnavaNumber: any[];
    nnavaNumberPosted: null;
    navaAccount: any[];
    account: any[];
    maxNumber: null;
    lastPrefix: string;
  } => ({
    error: null,
    nnavaNumber: [],
    nnavaNumberPosted: null,
    navaAccount: [],
    account: [],
    maxNumber: null,
    lastPrefix: '',
  }),
  getters: {
    getOperatorName:
      (state: IUsersOperatorDefineStore) =>
      (value: string, fullData: boolean = false) => {
        if (!state.nnavaNumber) return value;
        const user = state.nnavaNumber.find((item: INNavaNumber) => {
          return (
            item.number == value ||
            item.uuid == value ||
            item.custoemr_uuid == value
          );
        });

        const route = routerPlugin.route;
        const showDeleteUserPathList = ['contacts', 'reports'];

        const returnValue = () =>
          value
            ? showDeleteUserPathList.includes(route.path?.split('/')[1])
              ? value
              : value
            : value;

        if (!user)
          if (state.account) {
            const userAccount = state.account.find((item: IAccount) => {
              return item.user == value;
            });

            return userAccount
              ? (userAccount.fullname ?? value)
              : returnValue();
          } else {
            return returnValue();
          }

        return fullData
          ? user
          : user.name || user.family
            ? `${user.name ?? ''} ${user.family ?? ''}`.trim()
            : user.number;
      },
    adminNumber: () => {
      const userStore = useUserStore();
      return userStore.user.adminNumber;
    },
  },
  actions: {
    async getNNavaNumber() {
      const toastStore = useToastStore();
      try {
        const { data, error } = await useFetch<INNavaNumber[]>(
          '/api/users/operator/nNavaNumber'
        );
        if (!data.value) return;
        this.nnavaNumber = data.value
          .map((item) => ({
            ...item,
            fullname: item.family ? item.name + ' ' + item.family : item.name,
            isAdmin: item.number == this.adminNumber,
          }))

          .sort((a, b) => {
            const aHasDash = String(a.number).includes('-');
            const bHasDash = String(b.number).includes('-');

            if (aHasDash && !bHasDash) return 1;
            if (!aHasDash && bHasDash) return -1;
            return 0;
          });
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }

        return true;
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async postNNavaNumber(params: INNavaNumber) {
      const toastStore = useToastStore();
      const usersListStore = useUsersListStore();
      const { subUsers } = usersListStore;

      try {
        await this.getMaxNumber();
        if (!this.nnavaNumber || this.nnavaNumber.length == 0) {
          await this.getNNavaNumber();
        }
        if (this.maxNumber?.max_number <= this.nnavaNumber?.length) {
          this.error = 'yourNumberOfAuthorizedNumbersIsOver';
          toastStore.showAlert({
            title: this.error,
            showCloseButton: true,
            timerProgressBar: true,
            type: 'error',
          });
          return false;
        }

        try {
          const res = await $fetch<INNavaNumberPosted>(
            '/api/users/operator/nNavaNumberPost',
            {
              method: 'POST',
              body: {
                ...params,
              },
            }
          );

          if (res?.data) {
            this.nnavaNumberPosted = {
              guid: res.data,
            };
            this.nnavaNumberPosted.navanumber = params;
            return true;
          } else {
            toastStore.showAlert({
              title: 'errorInUserManagement',
              showCloseButton: true,
              timerProgressBar: true,
              type: 'error',
            });
          }

          return false;
        } catch (err: any) {
          let errorMessage = 'canNotConnectToServer';

          if (err.data?.data) {
            if (err.data?.data?.status === 3006) errorMessage = 'ipbx3006';
            else if (err.data?.data?.status === 3005) errorMessage = 'ipbx3005';
          }

          toastStore.showAlert({
            title: errorMessage,
            showCloseButton: true,
            timerProgressBar: true,
            type: 'error',
          });
          this.error = err;
          if (err && err.value?.statusCode) {
            throw err;
          }
          return false;
        }

        /*
                const { data, error } = await useFetch<INNavaNumberPosted>('/api/users/operator/nNavaNumberPost', {
                    method: 'POST',
                    body: params
                });
                this.error = error;
                if (error && error.value?.statusCode) {
                    throw error;
                }

                this.nnavaNumberPosted = data.value;
                this.nnavaNumberPosted.navanumber = params;
                return true
                */
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async postNNavaNumberVerify(params: INNavaNumberVerify) {
      const toastStore = useToastStore();

      try {
        const res = await $fetch<any>(
          '/api/users/operator/nNavaNumberVerifyPost',
          {
            method: 'POST',
            body: params,
          }
        );

        if (res === 'OK') {
          await this.getNNavaNumber();
          toastStore.showAlert({
            title: 'nnavanumberInsertSuccessfully',
            showCloseButton: true,
            timerProgressBar: true,
          });
          return true;
        }

        return false;
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async deleteNNavaNumber(params: INNavaNumber) {
      const toastStore = useToastStore();
      try {
        const { error } = await useFetch<any>(
          '/api/users/operator/nNavaNumberDelete',
          {
            method: 'DELETE',
            body: params,
          }
        );
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }

        this.nnavaNumber = this.nnavaNumber?.filter(
          (item) => item.number != params.number
        );
        toastStore.showAlert({
          title: 'nnavanumberDeleteSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async putNNavaNumber(params: INNavaNumber) {
      const usersListStore = useUsersListStore();
      const { subUsers } = usersListStore;

      const toastStore = useToastStore();
      try {
        const { error } = await useFetch<any>(
          '/api/users/operator/nNavaNumberPut',
          {
            method: 'PUT',
            body: params,
          }
        );
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }

        let index = this.nnavaNumber.findIndex(
          (item) => item.number == params.phoneNumber
        );
        this.nnavaNumber[index].name = params.firstName;
        this.nnavaNumber[index].family = params.lastName;
        this.nnavaNumber[index].internals = params.internal;

        const targetUser = subUsers?.find(
          (user) => user.phoneNumber == params.phoneNumber
        );
        if (targetUser) {
          Object.assign(targetUser, {
            role: params.role,
          });
        }
        toastStore.showAlert({
          title: 'nnavanumberUpdateSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },

    async getNavaAccount() {
      const toastStore = useToastStore();
      try {
        const { data, error } = await useFetch<INavaAccount[]>(
          '/api/users/operator/navaAccount'
        );
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }
        if (!data.value) return true;
        this.navaAccount = data.value.map(({ user, ...rest }) => ({
          ...rest,
          userName: user,
        }));
        return true;
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async postNavaAccount(params: INavaAccount) {
      const toastStore = useToastStore();
      const usersListStore = useUsersListStore();
      const { subUsers } = usersListStore;

      try {
        await this.getMaxNumber();
        if (!this.nnavaNumber || this.nnavaNumber.length == 0) {
          await this.getNNavaNumber();
        }
        if (this.maxNumber?.max_number <= this.nnavaNumber?.length) {
          this.error = 'yourNumberOfAuthorizedNumbersIsOver';
          toastStore.showAlert({
            title: this.error,
            showCloseButton: true,
            timerProgressBar: true,
            type: 'error',
          });
          return false;
        }

        const { prefix, userName, ...body } = params;
        try {
          const data = await $fetch<INNavaNumberPosted>(
            '/api/users/operator/navaAccountPost',
            {
              method: 'POST',
              body: body,
            }
          );
          params.id = data;
          params.userName = params.user;
          delete params.user;
          if (!this.navaAccount) this.navaAccount = [];

          subUsers.push({
            uuid: '',
            phoneNumber: params.userName,
            role: params.role,
          });

          this.navaAccount.push(params);
          toastStore.showAlert({
            title: 'navaaccountInsertSuccessfully',
            showCloseButton: true,
            timerProgressBar: true,
          });

          // prefix
          try {
            if (!prefix) return false;

            const res = await this.handlePrefix({
              data: {
                prefix: prefix,
              },
              method: 'POST',
            });
            if (res) this.lastPrefix = prefix;
          } catch (err: any) {
            console.error('error in api set default prefix : ', err);
          }

          return true;
        } catch (err: any) {
          const isDuplicate = err?.data?.data?.error.includes('Duplicate');
          toastStore.showAlert({
            title: isDuplicate
              ? 'theEnteredUsernameIsDuplicate'
              : 'canNotConnectToServer',
            showCloseButton: true,
            timerProgressBar: true,
            type: 'error',
          });

          this.error = err;
          if (err && err.value?.statusCode) {
            throw err;
          }
          return false;
        }

        /*
                const { data, error } = await useFetch<any>('/api/users/operator/navaAccountPost', {
                    method: 'POST',
                    body: params
                });
                this.error = error;
                if (error && error.value?.statusCode) {
                    throw error;
                }

        params.id = data.value;
        if (!this.navaAccount) this.navaAccount = [];
        this.navaAccount.push(params);
        toastStore.showAlert({
          title: "navaaccountInsertSuccessfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
                */
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async deleteNavaAccount(params: INavaAccount) {
      const toastStore = useToastStore();
      try {
        const { error } = await useFetch<any>(
          '/api/users/operator/navaAccountDelete',
          {
            method: 'DELETE',
            body: params,
          }
        );
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }

        this.navaAccount = this.navaAccount?.filter(
          (item) => item.id != params.id
        );
        toastStore.showAlert({
          title: 'navaaccountDeleteSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async putNavaAccount(params: any) {
      const toastStore = useToastStore();
      const usersListStore = useUsersListStore();
      const { subUsers } = usersListStore;
      const { prefix, userName, ...body } = params;

      try {
        const data = await $fetch<any>('/api/users/operator/navaAccountPut', {
          method: 'PUT',
          body: body,
        });

        const userAccount = this.navaAccount.find(
          (item) => item.id == params.id
        );
        if (userAccount) {
          Object.assign(userAccount, {
            ...params,
          });
        }

        const targetUser = subUsers?.find(
          (user) => user.phoneNumber == params.user
        );
        if (targetUser) {
          Object.assign(targetUser, {
            role: params.role,
          });
        }

        toastStore.showAlert({
          title: 'nnavanumberUpdateSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });

        // prefix
        try {
          if (!prefix) return false;

          const res = await this.handlePrefix({
            data: {
              prefix: prefix,
            },
            method: 'POST',
          });
          if (res) this.lastPrefix = prefix;
        } catch (err: any) {
          console.error('error in api set default prefix : ', err);
        }

        return true;
      } catch (err: any) {
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },

    async getAccount() {
      const toastStore = useToastStore();
      try {
        const { data, error } = await useFetch<IAccount[]>(
          '/api/users/operator/account'
        );
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }

        this.account = data.value
          ? data.value.map((item) => ({
              ...item,
              fullname:
                !item.firstname && !item.lastname
                  ? ''
                  : `${item.firstname || ''} ${item.lastname || ''}`,
            }))
          : [];

        return true;
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async postAccount(params: IAccount) {
      const toastStore = useToastStore();
      try {
        await this.getMaxNumber();
        if (!this.nnavaNumber || this.nnavaNumber.length == 0) {
          await this.getNNavaNumber();
        }
        if (this.maxNumber?.max_number <= this.nnavaNumber?.length) {
          this.error = 'yourNumberOfAuthorizedNumbersIsOver';
          toastStore.showAlert({
            title: this.error,
            showCloseButton: true,
            timerProgressBar: true,
            type: 'error',
          });
          return;
        }

        const { prefix, ...body } = params;
        try {
          const data = await $fetch<INNavaNumberPosted>(
            '/api/users/operator/accountPost',
            {
              method: 'POST',
              body: body,
            }
          );

          params.id = data;
          if (!this.account) this.account = [];
          this.account.push(params);
          toastStore.showAlert({
            title: 'accountInsertSuccessfully',
            showCloseButton: true,
            timerProgressBar: true,
          });

          // prefix
          // try {
          //   if (!prefix) return false;
          //
          //   const res = await this.handlePrefix({
          //     data: {
          //       prefix: prefix,
          //     },
          //     method: 'POST',
          //   });
          //   if (res) this.lastPrefix = prefix;
          // } catch (err: any) {
          //   console.error('error in api set default prefix : ', err);
          // }

          return true;
        } catch (err: any) {
          const errorMessage = err?.data?.data?.error ?? '';
          const isDuplicate = errorMessage.includes('Duplicate');
          toastStore.showAlert({
            title: isDuplicate
              ? 'theEnteredNumberIsDuplicate'
              : 'Can Not connect to server',
            showCloseButton: true,
            timerProgressBar: true,
            type: 'error',
          });

          this.error = err;
          if (err && err.value?.statusCode) {
            throw err;
          }
        }

        /*
                const { data, error } = await useFetch<any>('/api/users/operator/accountPost', {
                    method: 'POST',
                    body: params
                });
                this.error = error;
                if (error && error.value?.statusCode) {
                    throw error;
                }

                params.id = data.value;
                if (!this.account) this.account = [];
                this.account.push(params);
                toastStore.showAlert({
                    title: "accountInsertSuccessfully",
                    showCloseButton: true,
                    timerProgressBar: true,
                });
                return true
                */
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async deleteAccount(params: IAccount) {
      const toastStore = useToastStore();
      try {
        const { error } = await useFetch<any>(
          '/api/users/operator/accountDelete',
          {
            method: 'DELETE',
            body: params,
          }
        );
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }

        this.account = this.account?.filter((item) => item.id != params.id);
        toastStore.showAlert({
          title: 'accountDeleteSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async putAccount(params: any) {
      const toastStore = useToastStore();
      const { prefix, ...body } = params;

      try {
        const data = await $fetch<any>('/api/users/operator/accountPut', {
          method: 'PUT',
          body: body,
        });
        const userAccount = this.account.find((item) => item.id == params.id);
        if (userAccount) {
          Object.assign(userAccount, {
            ...params,
          });
        }
        toastStore.showAlert({
          title: 'nnavanumberUpdateSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });

        // prefix
        // try {
        //   if (!prefix) return false;
        //
        //   const res = await this.handlePrefix({
        //     data: {
        //       prefix: prefix,
        //     },
        //     method: 'POST',
        //   });
        //   if (res) this.lastPrefix = prefix;
        // } catch (err: any) {
        //   console.error('error in api set default prefix : ', err);
        // }

        return true;
      } catch (err: any) {
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },

    async getMaxNumber() {
      const toastStore = useToastStore();
      try {
        this.maxNumber = { max_number: 0 };
        const { data, error } = await useFetch<IMaxNumber>(
          '/api/users/operator/maxNumber'
        );
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }

        this.maxNumber = data.value;
        return true;
      } catch (err: any) {
        this.error = err;
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },

    async handlePrefix(body: { data: any; method: 'POST' | 'GET' }) {
      if (!body.method) return;

      try {
        const response: { prefix: string } | { ok: boolean } | null =
          await $fetch(`/api/users/operator/settings`, {
            method: 'POST',
            body: body,
          });
        if (response) {
          if (body.method === 'GET') {
            this.lastPrefix = response.prefix ?? this.adminNumber;
            return true;
          } else {
            return response.ok ?? false;
          }
        } else {
          if (body.method === 'GET') this.lastPrefix = this.adminNumber;
          return false;
        }
      } catch (err: any) {
        if (body.method === 'GET') this.lastPrefix = this.adminNumber;
        console.log('handle set prefix error', err);
        return false;
      }
    },
  },

  persist: {
    storage: persistedState.localStorage,
  },
});
